import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Link } from 'react-router-dom';
import PageLayout from '@/components/feature/PageLayout';
import DualActionFooter from '@/components/feature/DualActionFooter';
import ClientLogosStrip from '@/components/feature/ClientLogosStrip';
import HowWeVetEngineers from '@/components/feature/HowWeVetEngineers';
import TalentFinderSection from './components/TalentFinderSection';
import { services } from '@/mocks/services';

const generateServiceSchema = () => JSON.stringify({
  "@context": "https://schema.org",
  "@type": "Service",
  "name": "Talent Solutions & Staff Augmentation",
  "provider": { "@type": "Organization", "name": "Thoughtic Solutions", "url": "https://thoughtic.com" },
  "description": "Hire pre-vetted remote developers and engineers. Dedicated teams, contract staffing, offshore squads, and project-based engagements with 48-hour matching guarantee.",
  "serviceType": "IT Staff Augmentation",
  "url": "https://thoughtic.com/talent-solutions",
  "areaServed": { "@type": "Country", "name": "Worldwide" }
});

const springTransition = {
  type: 'spring',
  stiffness: 80,
  damping: 20,
};

// ── Lead Capture Form ──
function LeadCaptureForm() {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState<'success' | 'error'>('success');

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const data = new URLSearchParams();
    for (const [key, value] of formData.entries()) {
      data.append(key, value as string);
    }
    fetch(form.action, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: data.toString(),
    })
      .then(() => {
        setModalType('success');
        setShowModal(true);
        form.reset();
        // Google Analytics conversion event
        if (typeof window !== 'undefined' && (window as Record<string, unknown>).gtag) {
          (window as Record<string, unknown>).gtag('event', 'generate_lead', {
            event_category: 'talent_solutions',
            event_label: 'talent_match_request',
            value: 1,
          });
        }
      })
      .catch(() => {
        setModalType('error');
        setShowModal(true);
      });
  };

  return (
    <section id="cta" className="py-16 md:py-24 bg-navy-900 relative overflow-hidden" ref={ref}>
      {/* Thank You Modal */}
      <AnimatePresence>
        {showModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm px-4"
            onClick={() => setShowModal(false)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.85, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.85, y: 20 }}
              transition={{ duration: 0.4, type: 'spring', bounce: 0.3 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-2xl md:rounded-3xl p-8 md:p-12 max-w-md w-full shadow-2xl text-center"
            >
              {modalType === 'success' ? (
                <>
                  <motion.div
                    initial={{ opacity: 0, scale: 0.5 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.15, ...springTransition }}
                    className="w-20 h-20 md:w-24 md:h-24 flex items-center justify-center bg-primary-500 rounded-full mx-auto mb-6 shadow-lg"
                  >
                    <motion.i
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ delay: 0.2, type: 'spring', bounce: 0.5 }}
                      className="ri-check-line text-4xl md:text-5xl text-white"
                    ></motion.i>
                  </motion.div>
                  <h3 className="text-2xl md:text-3xl font-bold text-navy-900 mb-3">Request Received!</h3>
                  <p className="text-base md:text-lg text-gray-600 mb-8 leading-relaxed">
                    Our talent team will review your requirements and send matched candidate profiles within 48 hours.
                  </p>
                </>
              ) : (
                <>
                  <div className="w-20 h-20 md:w-24 md:h-24 flex items-center justify-center bg-red-500 rounded-full mx-auto mb-6 shadow-lg">
                    <motion.i
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ delay: 0.2, type: 'spring', bounce: 0.5 }}
                      className="ri-error-warning-line text-4xl md:text-5xl text-white"
                    ></motion.i>
                  </div>
                  <h3 className="text-2xl md:text-3xl font-bold text-navy-900 mb-3">Oops!</h3>
                  <p className="text-base md:text-lg text-gray-600 mb-8 leading-relaxed">
                    Something went wrong. Please try again or email us directly at{' '}
                    <a href="mailto:info@thoughtic.com" className="text-primary-600 hover:underline">info@thoughtic.com</a>.
                  </p>
                </>
              )}
              <button
                onClick={() => setShowModal(false)}
                className="px-8 py-3 bg-primary-500 text-white text-base font-semibold rounded-xl hover:shadow-xl hover:shadow-primary-500/25 hover:-translate-y-0.5 hover:brightness-110 transition-all duration-300 cursor-pointer whitespace-nowrap group"
              >
                {modalType === 'success' ? 'Got It' : 'Try Again'}
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }}></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
          {/* Left Content */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ ...springTransition, duration: 0.7 }}
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 border border-white/20 rounded-full mb-4 md:mb-6">
              <i className="ri-user-add-line text-primary-400 w-5 h-5 flex items-center justify-center"></i>
              <span className="text-sm font-medium text-white/80">Scale Your Team</span>
            </div>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-display font-bold text-white leading-tight mb-4 md:mb-6">
              Request Pre-Vetted
              <span className="text-primary-400"> Talent Matches</span>
            </h2>
            <p className="text-base md:text-lg text-white/70 leading-relaxed mb-6 md:mb-8">
              Tell us what roles you need, your tech stack, and timeline. Our talent team will hand-pick engineers who match your requirements and send their profiles within 48 hours — no obligation.
            </p>
            <div className="space-y-3 md:space-y-4">
              {[
                '48-hour candidate shortlist guarantee',
                'Pre-vetted, interview-ready profiles',
                'Risk-free 14-day replacement policy',
                'Transparent hourly and monthly rates',
              ].map((feature, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -30 }}
                  animate={inView ? { opacity: 1, x: 0 } : {}}
                  transition={{ ...springTransition, duration: 0.5, delay: 0.35 + index * 0.1 }}
                  className="flex items-center gap-3"
                >
                  <div className="w-5 h-5 flex items-center justify-center bg-primary-500 rounded-full flex-shrink-0">
                    <i className="ri-check-line text-xs text-white"></i>
                  </div>
                  <span className="text-sm text-gray-300">{feature}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Right Form */}
          <motion.div
            initial={{ opacity: 0, x: 30, scale: 0.95 }}
            animate={inView ? { opacity: 1, x: 0, scale: 1 } : {}}
            transition={{ ...springTransition, duration: 0.8, delay: 0.3 }}
            className="bg-white rounded-xl md:rounded-2xl p-6 md:p-8 shadow-xl"
          >
            <div className="text-center mb-4 md:mb-6">
              <h3 className="text-xl md:text-2xl font-bold text-navy-900 mb-2">Request Talent Matches</h3>
              <p className="text-xs md:text-sm text-gray-600">First candidate profiles in 48 hours</p>
            </div>

            <form
              className="space-y-3 md:space-y-4"
              method="POST"
              action="https://readdy.ai/api/form/d89b7c04og5fkb1kt6r0"
              encType="application/x-www-form-urlencoded"
              data-readdy-form
              onSubmit={handleSubmit}
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4">
                <div>
                  <label htmlFor="tl-name" className="block text-xs md:text-sm font-medium text-gray-700 mb-1.5">
                    Full Name
                  </label>
                  <input
                    type="text"
                    id="tl-name"
                    name="name"
                    className="w-full px-3 md:px-4 py-2.5 md:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all text-sm"
                    placeholder="John Doe"
                    required
                  />
                </div>
                <div>
                  <label htmlFor="tl-email" className="block text-xs md:text-sm font-medium text-gray-700 mb-1.5">
                    Work Email
                  </label>
                  <input
                    type="email"
                    id="tl-email"
                    name="email"
                    className="w-full px-3 md:px-4 py-2.5 md:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all text-sm"
                    placeholder="you@company.com"
                    required
                  />
                </div>
              </div>

              <div>
                <label htmlFor="tl-company" className="block text-xs md:text-sm font-medium text-gray-700 mb-1.5">
                  Company Name
                </label>
                <input
                  type="text"
                  id="tl-company"
                  name="company"
                  className="w-full px-3 md:px-4 py-2.5 md:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all text-sm"
                  placeholder="Acme Inc."
                  required
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4">
                <div>
                  <label htmlFor="tl-role" className="block text-xs md:text-sm font-medium text-gray-700 mb-1.5">
                    Role Needed
                  </label>
                  <select
                    id="tl-role"
                    name="role_needed"
                    className="w-full px-3 md:px-4 py-2.5 md:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all text-sm cursor-pointer"
                    required
                  >
                    <option value="">Select role</option>
                    <option value="React / Next.js Developer">React / Next.js Developer</option>
                    <option value="Node.js Developer">Node.js Developer</option>
                    <option value="Python / Django Developer">Python / Django Developer</option>
                    <option value="Full Stack Developer">Full Stack Developer</option>
                    <option value="DevOps / SRE Engineer">DevOps / SRE Engineer</option>
                    <option value="UI/UX Designer">UI/UX Designer</option>
                    <option value="QA / Automation Engineer">QA / Automation Engineer</option>
                    <option value="Mobile Developer (iOS/Android)">Mobile Developer (iOS/Android)</option>
                    <option value="AI / ML Engineer">AI / ML Engineer</option>
                    <option value="Project Manager / Scrum Master">Project Manager / Scrum Master</option>
                    <option value="Data Engineer">Data Engineer</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                <div>
                  <label htmlFor="tl-engagement" className="block text-xs md:text-sm font-medium text-gray-700 mb-1.5">
                    Engagement Type
                  </label>
                  <select
                    id="tl-engagement"
                    name="engagement_type"
                    className="w-full px-3 md:px-4 py-2.5 md:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all text-sm cursor-pointer"
                    required
                  >
                    <option value="">Select type</option>
                    <option value="Dedicated Developer">Dedicated Developer</option>
                    <option value="Contract Staffing">Contract Staffing</option>
                    <option value="Offshore Team">Offshore Team</option>
                    <option value="Project-Based Squad">Project-Based Squad</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4">
                <div>
                  <label htmlFor="tl-tech" className="block text-xs md:text-sm font-medium text-gray-700 mb-1.5">
                    Tech Stack
                  </label>
                  <input
                    type="text"
                    id="tl-tech"
                    name="tech_stack"
                    className="w-full px-3 md:px-4 py-2.5 md:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all text-sm"
                    placeholder="e.g. React, Node.js, AWS"
                  />
                </div>
                <div>
                  <label htmlFor="tl-timeline" className="block text-xs md:text-sm font-medium text-gray-700 mb-1.5">
                    Start Timeline
                  </label>
                  <select
                    id="tl-timeline"
                    name="timeline"
                    className="w-full px-3 md:px-4 py-2.5 md:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all text-sm cursor-pointer"
                  >
                    <option value="">Select timeline</option>
                    <option value="Immediately">Immediately</option>
                    <option value="Within 2 weeks">Within 2 weeks</option>
                    <option value="Within 1 month">Within 1 month</option>
                    <option value="1 - 3 months">1 – 3 months</option>
                    <option value="Not sure yet">Not sure yet</option>
                  </select>
                </div>
              </div>

              <div>
                <label htmlFor="tl-source" className="block text-xs md:text-sm font-medium text-gray-700 mb-1.5">
                  How Did You Find Us?
                </label>
                <select
                  id="tl-source"
                  name="source"
                  className="w-full px-3 md:px-4 py-2.5 md:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all text-sm cursor-pointer"
                >
                  <option value="">Select source</option>
                  <option value="Google Search">Google Search</option>
                  <option value="LinkedIn">LinkedIn</option>
                  <option value="Referral">Referral</option>
                  <option value="Clutch">Clutch</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              <div>
                <label htmlFor="tl-requirements" className="block text-xs md:text-sm font-medium text-gray-700 mb-1.5">
                  Requirements <span className="text-gray-400 font-normal">(max 500 chars)</span>
                </label>
                <textarea
                  id="tl-requirements"
                  name="requirements"
                  rows={3}
                  maxLength={500}
                  className="w-full px-3 md:px-4 py-2.5 md:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all text-sm resize-none"
                  placeholder="Describe the role, experience level, team size, and any specific requirements..."
                ></textarea>
              </div>

              <button
                type="submit"
                className="w-full py-3 md:py-4 bg-primary-500 text-white text-sm md:text-base font-semibold rounded-lg hover:shadow-xl hover:shadow-primary-500/25 hover:-translate-y-0.5 hover:brightness-110 transition-all duration-300 cursor-pointer whitespace-nowrap group"
              >
                Request Talent Matches
                <i className="ri-arrow-right-line ml-2 group-hover:translate-x-1 transition-transform duration-300 inline-block"></i>
              </button>

              <p className="text-xs text-center text-gray-500 mt-3">
                By submitting, you agree to our{' '}
                <Link to="/terms" className="text-primary-600 hover:underline cursor-pointer">Terms</Link>
                {' '}and{' '}
                <Link to="/privacy" className="text-primary-600 hover:underline cursor-pointer">Privacy Policy</Link>
              </p>
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

/* ── Hero ── */
function Hero() {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });
  return (
    <div className="pt-24 md:pt-32 pb-16 md:pb-24 bg-navy-900 relative overflow-hidden" ref={ref}>
      <div className="absolute inset-0">
        <img
          src="https://readdy.ai/api/search-image?query=Abstract%20dark%20corporate%20background%20with%20interconnected%20glowing%20network%20nodes%20and%20warm%20amber%20light%20trails%20suggesting%20global%20talent%20connections%2C%20deep%20navy%20blue%20and%20warm%20gold%20color%20scheme%2C%20futuristic%20professional%20composition%20suitable%20for%20website%20hero%20banner%20with%20white%20text%20overlay%2C%20premium%20and%20modern%20feel&width=1600&height=900&seq=talent-hero-2&orientation=landscape"
          alt=""
          className="w-full h-full object-cover opacity-30"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-navy-900/60 via-navy-900/70 to-navy-900/90"></div>
      </div>
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute -top-32 -right-32 w-[600px] h-[600px] bg-amber-500/10 rounded-full blur-3xl"></div>
        <div className="absolute -bottom-32 -left-32 w-[500px] h-[500px] bg-primary-500/10 rounded-full blur-3xl"></div>
      </div>


      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, ease: 'easeOut' }}
          className="text-center max-w-4xl mx-auto"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={inView ? { opacity: 1, scale: 1 } : {}}
            transition={{ delay: 0.1, duration: 0.5 }}
            className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 border border-white/20 rounded-full mb-4 md:mb-6"
          >
            <i className="ri-user-star-line text-primary-400 w-5 h-5 flex items-center justify-center"></i>
            <span className="text-sm font-medium text-white/80">Talent Solutions</span>
          </motion.div>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.15, duration: 0.6 }}
            className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-display font-bold text-white leading-tight mb-4 md:mb-6"
          >
            Hire Elite Tech Talent
            <br />
            <span className="text-primary-400">On Demand</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.25, duration: 0.5 }}
            className="text-base md:text-xl text-white/70 max-w-2xl mx-auto"
          >
            Dedicated developers, contract staffing, offshore teams, and project-based squads — pre-vetted, interview-ready, and matched to your stack in 48 hours.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.35, duration: 0.5 }}
            className="mt-8 md:mt-10 flex flex-col sm:flex-row items-center justify-center gap-4"
          >
            <a
              href="#cta"
              className="px-8 py-4 bg-primary-500 text-white text-base font-semibold rounded-xl hover:shadow-xl hover:shadow-primary-500/25 hover:-translate-y-0.5 hover:brightness-110 transition-all duration-300 cursor-pointer whitespace-nowrap group"
            >
              Request Talent Matches
              <i className="ri-arrow-right-line ml-2 group-hover:translate-x-1 transition-transform inline-block"></i>
            </a>
            <Link
              to="/how-it-works"
              className="px-8 py-4 border-2 border-white/30 text-white text-base font-semibold rounded-xl hover:bg-white/10 hover:border-white/50 transition-all duration-300 cursor-pointer whitespace-nowrap"
            >
              See How It Works
            </Link>
          </motion.div>
        </motion.div>
      </div>
    </div>
  );
}

/* ── Stats Bar ── */
function StatsBar() {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });
  const stats = [
    { value: '200+', label: 'Vetted Engineers', icon: 'ri-user-star-line' },
    { value: '48h', label: 'Avg. Time to Match', icon: 'ri-time-line' },
    { value: '94%', label: 'First-Match Success', icon: 'ri-thumb-up-line' },
    { value: '4', label: 'Engagement Models', icon: 'ri-briefcase-line' },
  ];

  return (
    <section className="py-10 md:py-14 bg-white border-b border-gray-100" ref={ref}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
          {stats.map((stat, i) => (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, y: 15 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.1 + i * 0.08, ...springTransition }}
              className="text-center"
            >
              <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-amber-50 mx-auto mb-2">
                <i className={`${stat.icon} text-amber-600 text-lg`}></i>
              </div>
              <div className="text-2xl md:text-3xl font-bold text-navy-900 mb-0.5">{stat.value}</div>
              <div className="text-xs text-gray-500 font-medium">{stat.label}</div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ── Why Hire With Us ── */
function WhyHireWithUs() {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.05 });

  const reasons = [
    {
      icon: 'ri-shield-check-line',
      title: 'Pre-Vetted Engineers',
      desc: 'Only 12% of applicants pass our 4-stage screening. Every engineer is tested on live coding, system design, and communication.',
      stat: '12%',
      statLabel: 'acceptance rate',
    },
    {
      icon: 'ri-flashlight-line',
      title: '48-Hour Matching',
      desc: 'Submit your requirements and receive matched candidates within two business days. The fastest in the industry.',
      stat: '48h',
      statLabel: 'avg. turnaround',
    },
    {
      icon: 'ri-global-line',
      title: 'Global Coverage',
      desc: 'Engineers across the Americas, Europe, and APAC. Time zone alignment, cultural fit, and English fluency guaranteed.',
      stat: '24/7',
      statLabel: 'timezone coverage',
    },
    {
      icon: 'ri-loop-left-line',
      title: 'Risk-Free Replacement',
      desc: 'Not the right fit? We replace any engineer within the first 14 days at zero cost. No questions asked.',
      stat: '100%',
      statLabel: 'replacement guarantee',
    },
  ];

  return (
    <section className="py-16 md:py-24 bg-white relative overflow-hidden" ref={ref}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 40, scale: 0.95 }}
          animate={inView ? { opacity: 1, y: 0, scale: 1 } : {}}
          transition={{ ...springTransition, duration: 0.7 }}
          className="text-center mb-12 md:mb-16"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={inView ? { opacity: 1, scale: 1 } : {}}
            transition={{ delay: 0.1, ...springTransition }}
            className="inline-flex items-center space-x-2 px-4 py-2 bg-amber-50 border border-amber-200 rounded-full mb-4 md:mb-6 shadow-sm"
          >
            <i className="ri-award-line text-amber-600 w-5 h-5 flex items-center justify-center"></i>
            <span className="text-sm font-medium text-gray-700">Why Thoughtic Talent</span>
          </motion.div>
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.15, ...springTransition }}
            className="text-3xl sm:text-4xl md:text-5xl font-display font-bold text-navy-900 leading-tight mb-4 md:mb-6"
          >
            Hire With
            <span className="text-primary-600"> Confidence</span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.25, duration: 0.5 }}
            className="text-base md:text-xl text-gray-600 max-w-3xl mx-auto px-2"
          >
            We have built a talent network designed for speed, quality, and peace of mind. Here is what sets our engineers apart.
          </motion.p>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
          {reasons.map((r, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 25 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.3 + index * 0.08, ...springTransition }}
              className="group bg-gray-50 rounded-2xl p-6 md:p-7 border border-gray-100 hover:border-amber-200 hover:shadow-lg transition-all duration-300"
            >
              <div className="w-12 h-12 flex items-center justify-center bg-amber-500 rounded-xl mb-4 group-hover:scale-110 transition-transform duration-300">
                <i className={`${r.icon} text-xl text-white`}></i>
              </div>
              <div className="mb-3">
                <span className="text-2xl md:text-3xl font-bold text-amber-600">{r.stat}</span>
                <p className="text-xs text-gray-500 mt-0.5">{r.statLabel}</p>
              </div>
              <h3 className="text-base md:text-lg font-bold text-navy-900 mb-2">{r.title}</h3>
              <p className="text-sm text-gray-600 leading-relaxed">{r.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ── Talent Services Deep Dive ── */
function TalentServices() {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.05 });
  const talentServices = services.filter((s) => s.category === 'talent');

  return (
    <section className="py-16 md:py-24 bg-gray-50/50 relative overflow-hidden" ref={ref}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 40, scale: 0.95 }}
          animate={inView ? { opacity: 1, y: 0, scale: 1 } : {}}
          transition={{ ...springTransition, duration: 0.7 }}
          className="text-center mb-12 md:mb-16"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={inView ? { opacity: 1, scale: 1 } : {}}
            transition={{ delay: 0.1, ...springTransition }}
            className="inline-flex items-center space-x-2 px-4 py-2 bg-white border border-gray-200 rounded-full mb-4 md:mb-6 shadow-sm"
          >
            <i className="ri-briefcase-line text-amber-600 w-5 h-5 flex items-center justify-center"></i>
            <span className="text-sm font-medium text-gray-700">Engagement Models</span>
          </motion.div>
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.15, ...springTransition }}
            className="text-3xl sm:text-4xl md:text-5xl font-display font-bold text-navy-900 leading-tight mb-4 md:mb-6"
          >
            Flexible Talent
            <span className="text-primary-600"> Solutions</span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.25, duration: 0.5 }}
            className="text-base md:text-xl text-gray-600 max-w-3xl mx-auto px-2"
          >
            From a single contractor to a fully managed offshore squad — choose the model that fits your timeline, budget, and team structure.
          </motion.p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
          {talentServices.map((service, index) => (
            <motion.div
              key={service.id}
              initial={{ opacity: 0, y: 25, scale: 0.97 }}
              animate={inView ? { opacity: 1, y: 0, scale: 1 } : {}}
              transition={{ delay: 0.3 + index * 0.1, ...springTransition, duration: 0.5 }}
              className="group bg-white rounded-2xl border border-gray-100 hover:border-amber-300 hover:shadow-xl hover:shadow-amber-500/5 transition-all duration-500 overflow-hidden"
            >
              <div className="p-5 md:p-6">
                <div className="flex items-start gap-3 mb-4">
                  <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-lg bg-amber-500">
                    <i className={`${service.icon} text-white text-sm`}></i>
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="text-base md:text-lg font-bold text-navy-900 leading-snug">
                      {service.title}
                    </h4>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-sm font-bold text-amber-600">{service.stat}</span>
                      <span className="text-xs text-gray-400">{service.statLabel}</span>
                    </div>
                  </div>
                </div>

                <p className="text-sm text-gray-600 leading-relaxed mb-4">
                  {service.fullDescription}
                </p>

                <div className="space-y-2 mb-5">
                  {service.features.map((feature) => (
                    <div key={feature} className="flex items-start gap-2">
                      <div className="w-4 h-4 flex items-center justify-center rounded-full bg-amber-50 flex-shrink-0 mt-0.5">
                        <i className="ri-check-line text-[10px] text-amber-600"></i>
                      </div>
                      <span className="text-xs text-gray-600">{feature}</span>
                    </div>
                  ))}
                </div>

                <a
                  href="#cta"
                  className="inline-flex items-center gap-1.5 text-sm font-semibold text-amber-600 hover:text-amber-700 transition-colors cursor-pointer group/link"
                >
                  Discuss your needs
                  <i className="ri-arrow-right-line group-hover/link:translate-x-1 transition-transform duration-300"></i>
                </a>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ── Vetting Process ── */
function VettingProcess() {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.05 });

  const steps = [
    {
      num: '01',
      title: 'Resume & Portfolio Review',
      desc: 'We verify 3+ years of production experience, open-source contributions, and shipped product evidence.',
      icon: 'ri-file-list-3-line',
    },
    {
      num: '02',
      title: 'Live Technical Interview',
      desc: 'A 90-minute session covering system design, coding challenges, and problem-solving under pressure.',
      icon: 'ri-video-chat-line',
    },
    {
      num: '03',
      title: 'Code Challenge',
      desc: 'Candidates build a real-world feature using your preferred stack — reviewed by senior staff engineers.',
      icon: 'ri-code-s-slash-line',
    },
    {
      num: '04',
      title: 'Communication & Culture Fit',
      desc: 'We assess English fluency, remote discipline, and ability to articulate decisions to stakeholders.',
      icon: 'ri-user-voice-line',
    },
  ];

  return (
    <section className="py-16 md:py-24 bg-white relative overflow-hidden" ref={ref}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 40, scale: 0.95 }}
          animate={inView ? { opacity: 1, y: 0, scale: 1 } : {}}
          transition={{ ...springTransition, duration: 0.7 }}
          className="text-center mb-12 md:mb-16"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={inView ? { opacity: 1, scale: 1 } : {}}
            transition={{ delay: 0.1, ...springTransition }}
            className="inline-flex items-center space-x-2 px-4 py-2 bg-amber-50 border border-amber-200 rounded-full mb-4 md:mb-6 shadow-sm"
          >
            <i className="ri-shield-check-line text-amber-600 w-5 h-5 flex items-center justify-center"></i>
            <span className="text-sm font-medium text-gray-700">Quality Assurance</span>
          </motion.div>
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.15, ...springTransition }}
            className="text-3xl sm:text-4xl md:text-5xl font-display font-bold text-navy-900 leading-tight mb-4 md:mb-6"
          >
            How We Vet
            <span className="text-primary-600"> Every Engineer</span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.25, duration: 0.5 }}
            className="text-base md:text-xl text-gray-600 max-w-3xl mx-auto px-2"
          >
            Only 12% of applicants pass our full screening. Here is the 4-stage process every engineer endures before reaching your shortlist.
          </motion.p>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
          {steps.map((step, index) => (
            <motion.div
              key={step.num}
              initial={{ opacity: 0, y: 25 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.3 + index * 0.1, ...springTransition }}
              className="group bg-gray-50 rounded-2xl p-6 md:p-7 border border-gray-100 hover:border-amber-200 hover:shadow-lg transition-all duration-300"
            >
              <div className="text-3xl font-black text-amber-200 mb-3">{step.num}</div>
              <div className="w-12 h-12 flex items-center justify-center bg-amber-500 rounded-xl mb-4 group-hover:scale-110 transition-transform duration-300">
                <i className={`${step.icon} text-xl text-white`}></i>
              </div>
              <h3 className="text-base md:text-lg font-bold text-navy-900 mb-2">{step.title}</h3>
              <p className="text-sm text-gray-600 leading-relaxed">{step.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ── Roles Covered ── */
function RolesCovered() {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.05 });

  const roles = [
    { name: 'React / Next.js', level: 'Expert' },
    { name: 'Node.js', level: 'Expert' },
    { name: 'Python / Django', level: 'Expert' },
    { name: 'TypeScript', level: 'Expert' },
    { name: 'React Native', level: 'Advanced' },
    { name: 'Flutter', level: 'Advanced' },
    { name: 'AWS / GCP / Azure', level: 'Expert' },
    { name: 'Kubernetes', level: 'Advanced' },
    { name: 'DevOps / CI/CD', level: 'Expert' },
    { name: 'AI / ML Engineering', level: 'Advanced' },
    { name: 'UI/UX Design', level: 'Expert' },
    { name: 'QA Automation', level: 'Advanced' },
  ];

  return (
    <section className="py-16 md:py-24 bg-gray-50/50 relative overflow-hidden" ref={ref}>
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute -top-20 -right-20 w-[500px] h-[500px] bg-amber-100/40 rounded-full blur-3xl"></div>
        <div className="absolute -bottom-20 -left-20 w-[400px] h-[400px] bg-primary-100/40 rounded-full blur-3xl"></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <motion.div
          initial={{ opacity: 0, y: 40, scale: 0.95 }}
          animate={inView ? { opacity: 1, y: 0, scale: 1 } : {}}
          transition={{ ...springTransition, duration: 0.7 }}
          className="text-center mb-12 md:mb-16"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={inView ? { opacity: 1, scale: 1 } : {}}
            transition={{ delay: 0.1, ...springTransition }}
            className="inline-flex items-center space-x-2 px-4 py-2 bg-white border border-gray-200 rounded-full mb-4 shadow-sm"
          >
            <i className="ri-code-s-slash-line text-amber-600 w-5 h-5 flex items-center justify-center"></i>
            <span className="text-sm font-medium text-gray-700">Technology Coverage</span>
          </motion.div>
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.15, ...springTransition }}
            className="text-3xl sm:text-4xl md:text-5xl font-display font-bold text-navy-900 leading-tight mb-4 md:mb-6"
          >
            Specialties We
            <span className="text-primary-600"> Staff</span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.25, duration: 0.5 }}
            className="text-base md:text-xl text-gray-600 max-w-3xl mx-auto px-2"
          >
            Our network covers the full modern tech stack. Every candidate is assessed on production-grade skills, not just theoretical knowledge.
          </motion.p>
        </motion.div>

        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3 md:gap-4">
          {roles.map((role, i) => (
            <motion.div
              key={role.name}
              initial={{ opacity: 0, y: 15 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.2 + i * 0.04, ...springTransition }}
              className="group bg-white rounded-xl p-4 border border-gray-100 hover:border-amber-200 hover:shadow-md transition-all duration-300 text-center"
            >
              <div className="w-3 h-3 rounded-full bg-amber-500 mx-auto mb-3"></div>
              <div className="text-sm font-bold text-navy-900 mb-0.5">{role.name}</div>
              <div className="text-[10px] text-gray-500 font-medium">{role.level}</div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ── Engagement Pricing ── */
function PricingSection() {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.05 });

  const plans = [
    {
      name: 'Dedicated Developer',
      price: 'from $4,500/mo',
      description: 'A full-time engineer exclusively on your team, managed by you.',
      features: [
        '160 hours/month commitment',
        'Direct communication & standups',
        'Your tools, your workflows',
        'Long-term retention focus',
        'Flexible pause or swap',
      ],
      highlight: false,
    },
    {
      name: 'Offshore Squad',
      price: 'from $18,000/mo',
      description: 'A fully managed team of 4–6 engineers with a project lead and PM.',
      features: [
        '4–6 engineers + project lead',
        'Dedicated workspace & infrastructure',
        'Daily reporting & sprint planning',
        'ISO-aligned security practices',
        '40% avg. cost reduction vs. onshore',
      ],
      highlight: true,
    },
    {
      name: 'Project-Based',
      price: 'Fixed or T&M',
      description: 'Fixed-scope project delivery with defined milestones and handover.',
      features: [
        'Fixed scope & transparent pricing',
        'Milestone-based payments',
        'Complete delivery team',
        'Knowledge transfer & docs',
        '3-month warranty period',
      ],
      highlight: false,
    },
  ];

  return (
    <section className="py-16 md:py-24 bg-white relative overflow-hidden" ref={ref}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 40, scale: 0.95 }}
          animate={inView ? { opacity: 1, y: 0, scale: 1 } : {}}
          transition={{ ...springTransition, duration: 0.7 }}
          className="text-center mb-12 md:mb-16"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={inView ? { opacity: 1, scale: 1 } : {}}
            transition={{ delay: 0.1, ...springTransition }}
            className="inline-flex items-center space-x-2 px-4 py-2 bg-amber-50 border border-amber-200 rounded-full mb-4 md:mb-6 shadow-sm"
          >
            <i className="ri-price-tag-3-line text-amber-600 w-5 h-5 flex items-center justify-center"></i>
            <span className="text-sm font-medium text-gray-700">Transparent Pricing</span>
          </motion.div>
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.15, ...springTransition }}
            className="text-3xl sm:text-4xl md:text-5xl font-display font-bold text-navy-900 leading-tight mb-4 md:mb-6"
          >
            Engagement
            <span className="text-primary-600"> Pricing Models</span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.25, duration: 0.5 }}
            className="text-base md:text-xl text-gray-600 max-w-3xl mx-auto px-2"
          >
            No hidden fees. Choose the model that fits your budget, timeline, and management style.
          </motion.p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
          {plans.map((plan, index) => (
            <motion.div
              key={plan.name}
              initial={{ opacity: 0, y: 30, scale: 0.95 }}
              animate={inView ? { opacity: 1, y: 0, scale: 1 } : {}}
              transition={{ delay: 0.3 + index * 0.1, ...springTransition }}
              className={`group relative bg-white rounded-2xl border ${plan.highlight ? 'border-amber-300 shadow-xl shadow-amber-500/5' : 'border-gray-100'} hover:shadow-xl transition-all duration-500 p-6 md:p-8`}
            >
              {plan.highlight && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 bg-amber-500 text-white text-xs font-bold rounded-full shadow-md">
                  Most Popular
                </div>
              )}
              <h3 className="text-lg md:text-xl font-bold text-navy-900 mb-1">{plan.name}</h3>
              <div className="text-2xl md:text-3xl font-bold text-amber-600 mb-3">{plan.price}</div>
              <p className="text-sm text-gray-500 mb-6">{plan.description}</p>
              <div className="space-y-3">
                {plan.features.map((f) => (
                  <div key={f} className="flex items-start gap-2.5">
                    <div className="w-5 h-5 flex items-center justify-center rounded-full bg-amber-50 flex-shrink-0 mt-0.5">
                      <i className="ri-check-line text-[10px] text-amber-600"></i>
                    </div>
                    <span className="text-sm text-gray-600">{f}</span>
                  </div>
                ))}
              </div>
              <a
                href="#cta"
                className={`mt-6 block text-center py-3 rounded-xl text-sm font-semibold transition-all duration-300 cursor-pointer ${plan.highlight ? 'bg-amber-500 text-white hover:shadow-lg hover:shadow-amber-500/25 hover:-translate-y-0.5' : 'bg-gray-50 text-navy-900 hover:bg-gray-100 border border-gray-200'}`}
              >
                Get a Custom Quote
              </a>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ── Global Talent Network ── */
function GlobalCoverageSection() {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.05 });

  const regions = [
    { region: 'North America', cities: 'San Francisco, New York, Toronto', timezone: 'PST / EST', coverage: 'Real-time' },
    { region: 'Latin America', cities: 'São Paulo, Mexico City, Buenos Aires', timezone: 'BRT / CST', coverage: '2–4h overlap' },
    { region: 'Europe', cities: 'London, Berlin, Warsaw, Lisbon', timezone: 'GMT / CET', coverage: '4–6h overlap' },
    { region: 'Asia Pacific', cities: 'Bangalore, Singapore, Manila', timezone: 'IST / SGT', coverage: 'Follow-the-sun' },
  ];

  return (
    <section className="py-16 md:py-24 bg-gray-50/50 relative overflow-hidden" ref={ref}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 40, scale: 0.95 }}
          animate={inView ? { opacity: 1, y: 0, scale: 1 } : {}}
          transition={{ ...springTransition, duration: 0.7 }}
          className="text-center mb-12 md:mb-16"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={inView ? { opacity: 1, scale: 1 } : {}}
            transition={{ delay: 0.1, ...springTransition }}
            className="inline-flex items-center space-x-2 px-4 py-2 bg-white border border-gray-200 rounded-full mb-4 md:mb-6 shadow-sm"
          >
            <i className="ri-global-line text-amber-600 w-5 h-5 flex items-center justify-center"></i>
            <span className="text-sm font-medium text-gray-700">Global Coverage</span>
          </motion.div>
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.15, ...springTransition }}
            className="text-3xl sm:text-4xl md:text-5xl font-display font-bold text-navy-900 leading-tight mb-4 md:mb-6"
          >
            Talent Across
            <span className="text-primary-600"> Every Time Zone</span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.25, duration: 0.5 }}
            className="text-base md:text-xl text-gray-600 max-w-3xl mx-auto px-2"
          >
            Our network spans four continents so you can hire engineers who align with your working hours, not against them.
          </motion.p>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
          {regions.map((r, index) => (
            <motion.div
              key={r.region}
              initial={{ opacity: 0, y: 20, scale: 0.97 }}
              animate={inView ? { opacity: 1, y: 0, scale: 1 } : {}}
              transition={{ delay: 0.3 + index * 0.08, ...springTransition }}
              className="group bg-white rounded-2xl p-6 border border-gray-100 hover:border-amber-200 hover:shadow-md transition-all duration-300"
            >
              <div className="w-10 h-10 flex items-center justify-center bg-amber-500 rounded-xl mb-4 group-hover:scale-110 transition-transform duration-300">
                <i className="ri-map-pin-line text-white text-lg"></i>
              </div>
              <h3 className="text-base font-bold text-navy-900 mb-1">{r.region}</h3>
              <p className="text-xs text-gray-500 mb-3">{r.cities}</p>
              <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                <div>
                  <div className="text-[10px] text-gray-400 uppercase tracking-wider">Timezone</div>
                  <div className="text-xs font-semibold text-navy-900">{r.timezone}</div>
                </div>
                <div className="text-right">
                  <div className="text-[10px] text-gray-400 uppercase tracking-wider">Coverage</div>
                  <div className="text-xs font-semibold text-amber-600">{r.coverage}</div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ── Testimonials ── */
function TestimonialsSection() {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.05 });

  const testimonials = [
    {
      quote: "Thoughtic saved our launch timeline. Every hire was production-ready from day one. We scaled from 12 to 28 engineers in under two weeks.",
      author: "David Park",
      role: "VP Engineering",
      company: "Fortune 500 Retailer",
      metric: "16 engineers in 10 days",
    },
    {
      quote: "The 48-hour matching guarantee is real. We submitted our React and Node.js requirements on Monday and had three senior candidates by Wednesday.",
      author: "Lisa Rodriguez",
      role: "Engineering Manager",
      company: "SaaS Scale-up",
      metric: "3 candidates in 48h",
    },
    {
      quote: "We have tried every talent platform. Thoughtic is the only one where every single engineer passed our internal technical screen on the first try.",
      author: "James Whitfield",
      role: "CTO",
      company: "HealthTech Startup",
      metric: "100% screen pass rate",
    },
  ];

  return (
    <section className="py-16 md:py-24 bg-white relative overflow-hidden" ref={ref}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 40, scale: 0.95 }}
          animate={inView ? { opacity: 1, y: 0, scale: 1 } : {}}
          transition={{ ...springTransition, duration: 0.7 }}
          className="text-center mb-12 md:mb-16"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={inView ? { opacity: 1, scale: 1 } : {}}
            transition={{ delay: 0.1, ...springTransition }}
            className="inline-flex items-center space-x-2 px-4 py-2 bg-amber-50 border border-amber-200 rounded-full mb-4 md:mb-6 shadow-sm"
          >
            <i className="ri-chat-quote-line text-amber-600 w-5 h-5 flex items-center justify-center"></i>
            <span className="text-sm font-medium text-gray-700">Client Voices</span>
          </motion.div>
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.15, ...springTransition }}
            className="text-3xl sm:text-4xl md:text-5xl font-display font-bold text-navy-900 leading-tight mb-4 md:mb-6"
          >
            What Clients Say
            <span className="text-primary-600"> About Our Talent</span>
          </motion.h2>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
          {testimonials.map((t, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30, rotateX: 10 }}
              animate={inView ? { opacity: 1, y: 0, rotateX: 0 } : {}}
              transition={{ delay: 0.25 + index * 0.12, ...springTransition, duration: 0.6 }}
              className="group bg-gray-50 rounded-2xl p-6 md:p-7 border border-gray-100 hover:border-amber-200 hover:shadow-lg transition-all duration-300"
            >
              <div className="flex items-center gap-1 mb-4">
                {[...Array(5)].map((_, i) => (
                  <i key={i} className="ri-star-fill text-amber-400 text-sm"></i>
                ))}
              </div>
              <p className="text-sm md:text-base text-gray-600 leading-relaxed mb-6 italic">
                "{t.quote}"
              </p>
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-full bg-amber-500 flex items-center justify-center text-white text-sm font-bold">
                  {t.author.split(' ').map(n => n[0]).join('')}
                </div>
                <div>
                  <div className="text-sm font-bold text-navy-900">{t.author}</div>
                  <div className="text-xs text-gray-500">{t.role}, {t.company}</div>
                </div>
              </div>
              <div className="inline-flex items-center px-3 py-1 bg-amber-50 rounded-full">
                <span className="text-xs font-bold text-amber-600">{t.metric}</span>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ── Case Study Snippet ── */
function CaseStudySnippet() {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.05 });

  return (
    <section className="py-16 md:py-24 bg-navy-900 relative overflow-hidden" ref={ref}>
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }}></div>
      </div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ ...springTransition, duration: 0.7 }}
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 border border-white/20 rounded-full mb-4">
              <i className="ri-briefcase-line text-primary-400 w-5 h-5 flex items-center justify-center"></i>
              <span className="text-sm font-medium text-white/80">Client Success</span>
            </div>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-display font-bold text-white leading-tight mb-4">
              Enterprise Team
              <span className="text-primary-400"> Scaled in 10 Days</span>
            </h2>
            <p className="text-base md:text-lg text-white/70 leading-relaxed mb-6">
              A Fortune 500 retailer needed to scale their engineering team from 12 to 28 developers ahead of a holiday launch. We placed 16 pre-vetted engineers — React, Node.js, and DevOps specialists — who were fully onboarded and shipping code within 10 days.
            </p>
            <div className="grid grid-cols-3 gap-4 mb-8">
              <div>
                <div className="text-2xl font-bold text-primary-400">16</div>
                <div className="text-xs text-white/60">Engineers placed</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-primary-400">10 Days</div>
                <div className="text-xs text-white/60">To full productivity</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-primary-400">0</div>
                <div className="text-xs text-white/60">Replacements needed</div>
              </div>
            </div>
            <Link
              to="/case-studies"
              className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-white text-sm font-semibold rounded-xl hover:shadow-xl hover:shadow-primary-500/25 hover:-translate-y-0.5 transition-all duration-300 cursor-pointer whitespace-nowrap group"
            >
              Read Full Case Study
              <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform"></i>
            </Link>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ delay: 0.2, ...springTransition, duration: 0.7 }}
            className="relative"
          >
            <img
              src="https://readdy.ai/api/search-image?query=Modern%20enterprise%20office%20with%20diverse%20software%20engineering%20team%20collaborating%20at%20computers%2C%20multiple%20monitors%20showing%20code%20and%20dashboards%2C%20warm%20ambient%20lighting%2C%20professional%20corporate%20environment%2C%20shallow%20depth%20of%20field%2C%20premium%20photography%20style&width=800&height=600&seq=talent-case-1&orientation=landscape"
              alt="Enterprise team scaling case study"
              className="rounded-2xl shadow-2xl shadow-navy-900/30 w-full"
            />
            <div className="absolute -bottom-4 -left-4 md:-bottom-6 md:-left-6 bg-white rounded-xl p-4 shadow-xl border border-gray-100">
              <div className="flex items-center gap-2 mb-1">
                <i className="ri-star-fill text-amber-400 text-sm"></i>
                <i className="ri-star-fill text-amber-400 text-sm"></i>
                <i className="ri-star-fill text-amber-400 text-sm"></i>
                <i className="ri-star-fill text-amber-400 text-sm"></i>
                <i className="ri-star-fill text-amber-400 text-sm"></i>
              </div>
              <p className="text-xs text-gray-600">"Thoughtic saved our launch timeline. Every hire was production-ready."</p>
              <p className="text-[10px] text-gray-400 mt-0.5">— VP Engineering, Fortune 500 Retailer</p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

export default function TalentSolutionsPage() {
  useEffect(() => {
    if (typeof window !== 'undefined' && (window as Record<string, unknown>).gtag) {
      (window as Record<string, unknown>).gtag('event', 'page_view', {
        page_title: 'Talent Solutions & Staff Augmentation',
        page_location: window.location.href,
        page_path: '/talent-solutions',
        custom_parameter_1: 'talent_solutions_funnel',
      });
    }

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = generateServiceSchema();
    document.head.appendChild(script);
    return () => { document.head.removeChild(script); };
  }, []);

  return (
    <PageLayout
      title="Talent Solutions & Staff Augmentation — 48-Hour Hiring"
      description="Hire pre-vetted remote developers, engineers, and tech specialists. Dedicated teams, contract staffing, offshore squads, and project-based engagements. 48-hour matching guarantee."
      keywords="staff augmentation services, hire remote developers, tech talent solutions, dedicated engineering teams, contract developers, offshore development squads, 48-hour developer matching, IT staffing solutions, engineer recruitment"
    >
      <Hero />
      <StatsBar />
      <WhyHireWithUs />
      <TalentServices />
      <VettingProcess />
      <RolesCovered />
      <TalentFinderSection />
      <PricingSection />
      <GlobalCoverageSection />
      <TestimonialsSection />
      <HowWeVetEngineers
        accentColor="amber"
        heading="How We Vet Every Engineer"
        specialtyNote="Our talent network is built on rigorous screening — every engineer arrives pre-tested, compliance-aware, and matched to your exact tech stack and team culture."
      />
      <CaseStudySnippet />
      <LeadCaptureForm />
      <DualActionFooter />
    </PageLayout>
  );
}